import React, { useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Collapse,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";

const ForecastCard = ({ selectedModel, forecastResults, selectedTarget, columns, keyMapping }) => {
  const [show, setShow] = useState(true);

  if (!forecastResults) return null;

  // Find the display label for the target, falling back to the selectedTarget if not found.
  const targetLabel =
    (columns.find((col) => col.key === selectedTarget) || {}).name ||
    selectedTarget;

  // Invert keyMapping so that we map from actual key to display label.
  const invertedMapping =
    keyMapping && Object.keys(keyMapping).length
      ? Object.fromEntries(
          Object.entries(keyMapping).map(([display, actual]) => [actual, display])
        )
      : {};

  // --- NEW LOGIC: Analyze impacts to suggest which parameters have higher or lower effect. ---
  let sortedImpacts = [];
  let topPositive = null;
  let topNegative = null;

  if (
    forecastResults.impact_analysis &&
    forecastResults.impact_analysis.impacts
  ) {
    // Convert impacts object into an array of [predictor, coefficient].
    const impactArray = Object.entries(forecastResults.impact_analysis.impacts);
    // Sort descending by absolute coefficient.
    sortedImpacts = impactArray.sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));

    // Identify the top positive and top negative impacts (if any).
    topPositive = sortedImpacts.find(([_, coef]) => coef > 0);
    topNegative = sortedImpacts.find(([_, coef]) => coef < 0);
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.800"
      borderRadius="md"
      boxShadow="md"
      p={4}
      bg="white"
      mb={4}
    >
      {/* Header with toggle button */}
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="bold">
          {`ผลการทำนาย (Forecast) ${targetLabel}:`}
        </Text>
        <IconButton
          icon={show ? <ChevronUpIcon /> : <ChevronDownIcon />}
          onClick={() => setShow(!show)}
          variant="ghost"
          size="sm"
          aria-label="Toggle Collapse"
        />
      </Flex>

      {/* Collapsible content */}
      <Collapse in={show}>
        {/* Optionally, display the forecast plot image */}
        <Flex>
          <Box flex={4}>
            <img
              src={`data:image/png;base64,${forecastResults.plot_image_base64}`}
              alt="Forecast Plot"
              style={{ maxWidth: "100%" }}
            />
          </Box>
          <Box mt={10} flex={1}>
          <Text fontSize="xl" fontWeight="semibold" mb={2}>
            โมเดลที่ใช้: {selectedModel}
          </Text>
          
            <Table mt={5} size={"sm"} variant="striped" colorScheme="alphagray">
              <Thead bgColor="gray.200">
                <Tr>
                  <Th>
                    <Text fontSize={10}>ปีที่ทำนาย</Text>
                  </Th>
                  <Th>
                    <Text fontSize={10}>ค่าเฉลี่ย (ค่าที่ทำนาย)</Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {forecastResults.forecast_years.map((year, index) => (
                  <Tr key={year}>
                    <Td>{year}</Td>
                    <Td>{parseFloat(forecastResults.forecast[index]).toFixed(2)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Flex>

        {/* Impact Analysis Section */}
        <Box mt={4}>
          <Text mb={2} fontWeight="bold">
            ผลการวิเคราะห์ผลกระทบ (Impact Analysis):
          </Text>
          {forecastResults.impact_analysis &&
          forecastResults.impact_analysis.impacts ? (
            <>
              <Table
                maxW="1200px"
                borderColor="gray.800"
                borderWidth={1}
                variant="striped"
                colorScheme="gray"
                size="sm"
              >
                <Thead bgColor="blue.100">
                  <Tr>
                    <Th>
                      <Text fontSize={15}>ตัวแปร</Text>
                    </Th>
                    <Th>
                      <Text fontSize={15}>Coefficient</Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Object.entries(forecastResults.impact_analysis.impacts).map(
                    ([predictor, coef]) => {
                      const displayLabel = invertedMapping[predictor] || predictor;
                      return (
                        <Tr key={predictor}>
                          <Td>{displayLabel}</Td>
                          <Td>{coef}</Td>
                        </Tr>
                      );
                    }
                  )}
                </Tbody>
              </Table>

              {/* Suggestion or Explanation about impacts */}
              <Box
                mt={4}
                p={3}
                borderWidth="1px"
                borderColor="gray.300"
                borderRadius="md"
              >
                <Text fontWeight="bold" mb={2}>
                  คำแนะนำ:
                </Text>
                {sortedImpacts.length > 0 ? (
                  <Text fontSize="sm">
                    {topPositive && (
                      <>
                        <Text>
                          - ตัวแปร <b>{topPositive[0]}</b> มีค่าสัมประสิทธิ์เป็นบวก
                          (สูงสุดในด้านบวก) หมายถึง ถ้าตัวแปรนี้เพิ่มขึ้น จะทำให้{" "}
                          {targetLabel} เพิ่มขึ้น
                        </Text>
                      </>
                    )}
                    {topNegative && (
                      <>
                        <Text>
                          - ตัวแปร <b>{topNegative[0]}</b> มีค่าสัมประสิทธิ์เป็นลบ
                          (สูงสุดในด้านลบ) หมายถึง ถ้าตัวแปรนี้เพิ่มขึ้น จะทำให้{" "}
                          {targetLabel} ลดลง
                        </Text>
                      </>
                    )}
                    {topNegative && (
                      <>
                        <Text>
                          - การคำนวณสัมประสิทธิ์จะใช้ <b>OLS Regression</b> ในการวัดผลกระทบของตัวแปรต้นต่าง ๆ ที่มีต่อผลลัพธ์ (target) 
                        </Text>
                      </>
                    )}
                    <Text mt={2}>
                      โปรดพิจารณาค่าสัมประสิทธิ์ (Coefficient) เพื่อปรับกลยุทธ์ให้เหมาะสม
                      หากต้องการเพิ่ม/ลด {targetLabel} สามารถปรับเปลี่ยนตัวแปรเหล่านี้
                      ตามทิศทางที่ต้องการ
                    </Text>
                  </Text>
                ) : (
                  <Text fontSize="sm">
                    ไม่พบข้อมูลตัวแปรที่มีผลกระทบอย่างมีนัยสำคัญ
                  </Text>
                )}
              </Box>
            </>
          ) : (
            <Text fontSize="sm">ไม่มีข้อมูลผลกระทบให้แสดง</Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ForecastCard;
